import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Menu, X, User, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

const TABS = [
  { label: "Home", to: "/" },
  { label: "Sing", to: "/sing" },
  { label: "Clips", to: "/clips" },
  { label: "Episodes", to: "/episodes" },
  { label: "Stories", to: "/stories" },
  { label: "Rest", to: "/rest" },
  { label: "Community", to: "/community" },
  { label: "Activities", to: "/activities" },
  { label: "Shop", to: "/shop" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const { user, openAuth, logout } = useAuth();
  const { pathname } = useLocation();
  const signedIn = !!(user && user.user_id);
  const visibleTabs = signedIn ? TABS : TABS.filter((t) => ["/sing", "/clips", "/episodes", "/stories", "/shop"].includes(t.to));

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-nav">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mt-4 flex items-center justify-between rounded-full border border-[#E2DDD0] bg-[#FFFDF6]/85 px-4 sm:px-6 py-3 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.15)] backdrop-blur-xl">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#FFC107] shadow-inner">
              <Star className="h-5 w-5 fill-white text-white" />
            </span>
            <span className="font-display text-base sm:text-lg font-semibold tracking-tight">
              Little Songs. <span className="text-[#0284C7]">Big Faith.</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1" data-testid="nav-tabs">
            {visibleTabs.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`nav-tab-${t.label.toLowerCase()}`}
                className={`rounded-full px-3 py-2 text-[13px] font-bold transition-all ${
                  pathname === t.to ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user && user.user_id ? (
              <div className="flex items-center gap-2">
                <NotificationBell />
                <div className="relative">
                <button
                  onClick={() => setMenu((v) => !v)}
                  data-testid="nav-avatar-btn"
                  aria-label="Account menu"
                  className="grid h-10 w-10 place-items-center overflow-hidden rounded-full font-display text-base font-bold text-white shadow transition-transform hover:scale-105"
                  style={{ backgroundColor: user.avatar_color }}
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    user.name[0]
                  )}
                </button>
                <AnimatePresence>
                  {menu && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      className="absolute right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-[#E2DDD0] bg-white py-2 shadow-xl"
                      data-testid="nav-account-menu"
                    >
                      <Link
                        to="/profile"
                        onClick={() => setMenu(false)}
                        data-testid="nav-profile-link"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-[#FEF3C7]"
                      >
                        <User className="h-4 w-4" /> My profile
                      </Link>
                      {user.role === "admin" && (
                        <>
                          <Link
                            to="/admin"
                            onClick={() => setMenu(false)}
                            data-testid="nav-admin-link"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-[#FEF3C7]"
                          >
                            <ShieldCheck className="h-4 w-4" /> Moderation
                          </Link>
                          <Link
                            to="/admin/media"
                            onClick={() => setMenu(false)}
                            data-testid="nav-media-link"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-[#FEF3C7]"
                          >
                            <ShieldCheck className="h-4 w-4" /> Media library
                          </Link>
                        </>
                      )}
                      <button
                        onClick={async () => { setMenu(false); await logout(); }}
                        data-testid="nav-logout-btn"
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-[#DC2626] hover:bg-[#FFE5E5]"
                      >
                        <LogOut className="h-4 w-4" /> Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/signin"
                  data-testid="nav-signin-btn"
                  className="hidden sm:inline-flex items-center rounded-full px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:text-slate-900"
                >
                  Sign in
                </Link>
                <Link
                  to="/signin"
                  state={{ mode: "signup" }}
                  data-testid="nav-join-btn"
                  className="hidden sm:inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.04] active:scale-95"
                >
                  Join Free
                </Link>
              </>
            )}
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-[#E2DDD0] bg-white"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden mt-2 rounded-3xl border border-[#E2DDD0] bg-white p-4 shadow-xl"
              data-testid="nav-mobile-menu"
            >
              {visibleTabs.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-tab-${t.label.toLowerCase()}`}
                  className={`block rounded-2xl px-4 py-3 font-display text-lg font-medium hover:bg-[#FEF3C7] ${
                    pathname === t.to ? "bg-slate-900 text-white" : ""
                  }`}
                >
                  {t.label}
                </Link>
              ))}
              {user && user.user_id ? (
                <Link
                  to="/profile"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-profile-link"
                  className="mt-2 block rounded-2xl bg-slate-900 px-4 py-3 text-center font-display text-lg font-medium text-white"
                >
                  My profile
                </Link>
              ) : (
                <Link
                  to="/signin"
                  state={{ mode: "signup" }}
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-join-btn"
                  className="mt-2 block w-full rounded-2xl bg-slate-900 px-4 py-3 text-center font-display text-lg font-medium text-white"
                >
                  Join Free
                </Link>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Nav;
